import React, { memo, useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import type { NodeProps } from '@xyflow/react';
import { useTenantView } from '../TenantViewContext';
import { getNodePulseLevel, isNodeWiredToLunaCore } from '../pulseUtils';
import {
  Clock,
  Sparkles,
  Zap,
  ArrowRight,
  ShieldCheck,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Moon,
  MessageSquare,
  Gift,
  HelpCircle,
  Tag,
  AlertCircle,
  History,
  RefreshCw,
  Send,
  User
} from 'lucide-react';

export interface FollowUpNodeData {
  delayHours?: number; // 6, 12, 24, 48
  maxAttempts?: number; // default 1
  quietHours?: boolean; // default true (10 PM - 8 AM)
  includeIncentive?: boolean;
  incentiveText?: string;
  filterAbandonedInquiries?: boolean;
  filterAbandonedOrders?: boolean;
  filterUnfinishedForms?: boolean;
  customTemplate?: string;
  isEnabled?: boolean;
  [key: string]: unknown;
}

export const FollowUpNode: React.FC<NodeProps> = ({ id, data, selected }) => {
  const { setNodes, getEdges, getNodes } = useReactFlow();
  const nodeData = (data || {}) as FollowUpNodeData;

  const isEnabled = nodeData.isEnabled !== false;
  const delayHours = nodeData.delayHours ?? 24;
  const quietHours = nodeData.quietHours !== false;
  const includeIncentive = nodeData.includeIncentive === true;
  const incentiveText = nodeData.incentiveText ?? 'Free express delivery if confirmed today!';
  const filterAbandonedInquiries = nodeData.filterAbandonedInquiries !== false;
  const filterAbandonedOrders = nodeData.filterAbandonedOrders !== false;
  const filterUnfinishedForms = nodeData.filterUnfinishedForms !== false;
  const customTemplate = nodeData.customTemplate ?? '';

  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [saveIndicator, setSaveIndicator] = useState<string | null>(null);
  const debounceTimerRef = useRef<any>(null);

  // ── Live Follow-Up Telemetry & Pending Queue State ──
  const [telemetry, setTelemetry] = useState<any>(null);
  const [loadingTelemetry, setLoadingTelemetry] = useState(false);
  const [isQueueOpen, setIsQueueOpen] = useState(false);
  const [dispatchingId, setDispatchingId] = useState<string | null>(null);

  const { isTenantView, authToken, tenantApiKey, pulseState, openPortalData } = useTenantView();

  const pulseLevel = getNodePulseLevel(id, pulseState);
  const isNodeActive = pulseLevel === 'active';
  const isNodeTrail = pulseLevel === 'trail';
  const isWired = useMemo(() => isNodeWiredToLunaCore(id, getEdges(), getNodes()), [id, getEdges, getNodes]);

  const getAuthHeaders = useCallback(() => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    const token = authToken || (typeof localStorage !== 'undefined' ? localStorage.getItem('tenet_token') || localStorage.getItem('admin_token') : null);
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    if (tenantApiKey) {
      headers['x-tenant-api-key'] = tenantApiKey;
    }
    return headers;
  }, [authToken, tenantApiKey]);

  const fetchTelemetry = useCallback(async () => {
    setLoadingTelemetry(true);
    try {
      const endpoint = isTenantView ? '/api/tenet/followup/telemetry' : '/api/admin/luna-flows/follow-up/telemetry';
      const res = await fetch(endpoint, {
        headers: getAuthHeaders(),
      });
      const resData = await res.json();
      if (resData?.success && resData?.telemetry) {
        setTelemetry(resData.telemetry);
      }
    } catch (err) {
      console.error('[FollowUpNode] Telemetry fetch error:', err);
    } finally {
      setLoadingTelemetry(false);
    }
  }, [isTenantView, getAuthHeaders]);

  useEffect(() => {
    fetchTelemetry();
  }, [fetchTelemetry]);

  const handleDispatchSingle = async (conversationId: string) => {
    if (dispatchingId) return;
    setDispatchingId(conversationId);
    try {
      const endpoint = isTenantView ? '/api/tenet/followup/dispatch-single' : '/api/admin/luna-flows/follow-up/dispatch-single';
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ conversationId }),
      });
      const resData = await res.json();
      if (resData?.success) {
        if (resData.telemetry) {
          setTelemetry(resData.telemetry);
        } else {
          await fetchTelemetry();
        }
      }
    } catch (err) {
      console.error('[FollowUpNode] Dispatch single error:', err);
    } finally {
      setDispatchingId(null);
    }
  };

  const handleDismissCandidate = async (conversationId: string | number) => {
    try {
      const endpoint = isTenantView ? '/api/tenet/followup/dismiss' : '/api/admin/luna-flows/follow-up/dismiss';
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ conversationId }),
      });
      const resData = await res.json();
      if (resData?.success) {
        if (resData.telemetry) {
          setTelemetry(resData.telemetry);
        } else {
          await fetchTelemetry();
        }
      }
    } catch (err) {
      console.error('[FollowUpNode] Dismiss error:', err);
    }
  };

  const [isTestDrawerOpen, setIsTestDrawerOpen] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testTopic, setTestTopic] = useState('');
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string; text?: string } | null>(null);

  const handleSendTestMessage = async () => {
    if (!testPhone.trim()) return;
    setIsTesting(true);
    setTestResult(null);
    try {
      const endpoint = isTenantView ? '/api/tenet/followup/test-send' : '/api/admin/luna-flows/follow-up/test-send';
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          targetPhone: testPhone.trim(),
          testTopic: testTopic.trim() || 'product inquiry',
        }),
      });
      const data = await res.json();
      if (data.success) {
        setTestResult({ success: true, message: data.message || 'Dispatched!', text: data.followUpText });
      } else {
        setTestResult({ success: false, message: data.error || 'Failed' });
      }
    } catch (err: any) {
      setTestResult({ success: false, message: err?.message || 'Error' });
    } finally {
      setIsTesting(false);
    }
  };

  const persistNodeDataDirectly = (allUpdates: Partial<FollowUpNodeData>) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const payloadData = {
          ...nodeData,
          isEnabled,
          ...allUpdates
        };

        const headers = getAuthHeaders();
        const endpoint = isTenantView ? '/api/tenet/widgets/save-node-data' : '/api/admin/luna-flows/save-node-data';
        const res = await fetch(endpoint, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            nodeId: id,
            widgetType: 'FOLLOW_UP_RECOVERY',
            data: payloadData,
            widgetData: payloadData,
          }),
        });

        if (res.ok) {
          setSaveIndicator('Saved');
          setTimeout(() => setSaveIndicator(null), 1500);
        }
      } catch (err) {
        console.error('[FollowUpNode] Persist error:', err);
      }
    }, 400);
  };

  const handleFieldChange = (field: keyof FollowUpNodeData, value: any) => {
    setNodes((nds) =>
      nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, [field]: value } } : n))
    );
    persistNodeDataDirectly({ [field]: value });
  };

  return (
    <div
      className={`rounded-2xl border transition-all duration-300 w-[420px] select-none group relative backdrop-blur-xl bg-gradient-to-b from-[#140C22]/98 via-[#0A0612]/98 to-[#05030A]/98 shadow-[0_20px_50px_rgba(0,0,0,0.85),inset_0_1px_0_0_rgba(255,255,255,0.12)] ${
        isNodeActive
          ? 'border-cyan-300 ring-4 ring-cyan-400/60 shadow-[0_0_80px_rgba(34,211,238,0.8),inset_0_1px_0_0_rgba(255,255,255,0.2)] scale-[1.02] pulse-glow-active'
          : isNodeTrail
          ? 'border-emerald-400/70 ring-2 ring-emerald-400/30 shadow-[0_0_40px_rgba(52,211,153,0.4),inset_0_1px_0_0_rgba(255,255,255,0.15)]'
          : selected
          ? 'border-purple-400 ring-2 ring-purple-400/50 shadow-[0_0_35px_rgba(168,85,247,0.5),inset_0_1px_0_0_rgba(255,255,255,0.18)]'
          : isWired
          ? 'border-purple-500/40 hover:border-purple-400 shadow-[0_20px_50px_rgba(0,0,0,0.85),0_0_20px_rgba(168,85,247,0.15),inset_0_1px_0_0_rgba(255,255,255,0.12)]'
          : 'border-white/[0.08] hover:border-white/[0.18]'
      }`}
    >
      {/* ── Floating Cyber-HUD Action: View Recovery Queue in Portal ── */}
      {openPortalData && (
        <div className="absolute -top-11 left-1/2 -translate-x-1/2 z-50 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all duration-300 transform translate-y-1 group-hover:translate-y-0">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openPortalData('followup', id);
            }}
            className="nodrag flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-purple-950 via-slate-900 to-indigo-950 border-2 border-purple-400 text-purple-200 font-extrabold text-[11px] uppercase tracking-wider shadow-[0_0_25px_rgba(168,85,247,0.8)] hover:border-purple-300 hover:scale-105 hover:text-white transition-all cursor-pointer whitespace-nowrap backdrop-blur-md active:scale-95"
            title="Open Portal Data tab to view live recovery queue & abandoned conversation telemetry"
          >
            <Clock size={13} className="text-purple-300 animate-pulse" />
            <span>View Recovery Queue in Portal</span>
            <ArrowRight size={13} className="text-purple-400" />
          </button>
        </div>
      )}

      {/* ── Input Handle: Downstream Action Connection from Luna Core Hub ── */}
      <Handle
        type="target"
        position={Position.Left}
        id="in-followup"
        className="!bg-purple-400 !w-4 !h-4 !border-2 !border-slate-950 !-left-2 transition-transform hover:scale-125 z-20 shadow-[0_0_12px_rgba(168,85,247,1)]"
        title="Automated Follow-Up Trigger connection from Luna Core Hub"
      />

      {/* ── Header ── */}
      <div className="p-4 border-b border-purple-500/30 bg-gradient-to-r from-purple-950/80 via-slate-900/80 to-indigo-950/60 rounded-t-2xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-purple-500/20 border border-purple-500/40 text-purple-300 shadow-[0_0_15px_rgba(168,85,247,0.3)]">
              <Clock size={18} className={isEnabled ? 'animate-pulse' : ''} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-bold text-white tracking-wide">
                  Smart Follow-Up & Re-engagement
                </h3>
              </div>
              <p className="text-[11px] text-slate-400 font-sans mt-0.5">
                Autonomous Abandoned Chat & Cart Recovery
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Live Interactive Test Button */}
            <button
              type="button"
              onClick={() => setIsTestDrawerOpen(!isTestDrawerOpen)}
              className={`text-[10px] font-mono font-bold px-2 py-1 rounded-lg border transition cursor-pointer flex items-center gap-1 ${
                isTestDrawerOpen
                  ? 'bg-indigo-600 text-white border-indigo-400 shadow-[0_0_12px_rgba(99,102,241,0.6)]'
                  : 'bg-slate-900 border-purple-500/30 text-purple-300 hover:text-white hover:border-purple-400'
              }`}
              title="Test real AI re-engagement message to a test phone number"
            >
              <Sparkles size={11} className={isTestDrawerOpen ? 'animate-spin' : ''} />
              <span>TEST</span>
            </button>

            {/* Live Pending Queue Button */}
            <button
              type="button"
              onClick={() => setIsQueueOpen(!isQueueOpen)}
              className={`text-[10px] font-mono font-bold px-2.5 py-1 rounded-lg border transition cursor-pointer flex items-center gap-1.5 shadow-sm ${
                isQueueOpen
                  ? 'bg-purple-500 text-white border-purple-400 shadow-[0_0_15px_rgba(168,85,247,0.6)]'
                  : telemetry?.pendingQueue?.length
                  ? 'bg-purple-950/90 border-purple-400/60 text-purple-200 hover:border-purple-300'
                  : 'bg-slate-900 border-slate-800 text-slate-400 hover:text-slate-300'
              }`}
              title="Toggle Live Pending Follow-Up Queue"
            >
              <Clock size={11} className={telemetry?.pendingQueue?.length ? 'text-purple-300 animate-pulse' : 'text-slate-400'} />
              <span>{telemetry?.pendingQueue?.length ? `${telemetry.pendingQueue.length} QUEUED` : '0 QUEUED'}</span>
            </button>

            <button
              type="button"
              onClick={() => handleFieldChange('isEnabled', !isEnabled)}
              className={`text-[10px] font-mono font-bold px-2 py-1 rounded-lg border transition cursor-pointer ${
                isEnabled
                  ? 'bg-purple-500/20 border-purple-500/40 text-purple-300 shadow-sm'
                  : 'bg-slate-900 border-slate-800 text-slate-500 hover:text-slate-400'
              }`}
            >
              {isEnabled ? 'ACTIVE' : 'MUTED'}
            </button>
          </div>
        </div>

        {/* Wiring & Save Indicators */}
        <div className="flex items-center justify-between mt-2.5 pt-2 border-t border-purple-500/10 text-[10px] font-mono">
          <div className="flex items-center gap-1.5">
            <div className={`w-2 h-2 rounded-full ${isWired ? 'bg-emerald-400 animate-ping' : 'bg-rose-500'}`} />
            <span className={isWired ? 'text-emerald-300' : 'text-rose-400'}>
              {isWired ? 'Wired to Luna Core Hub' : 'Unwired (Connect to Core out-actions)'}
            </span>
          </div>
          {saveIndicator && <span className="text-emerald-400 animate-pulse">{saveIndicator}</span>}
        </div>
      </div>

      {/* ── Body ── */}
      <div className="p-4 space-y-3.5 text-xs text-slate-300">

        {/* ── Interactive Test Dispatcher Drawer ── */}
        {isTestDrawerOpen && (
          <div className="p-3 rounded-xl bg-indigo-950/40 border border-indigo-500/40 space-y-2.5 shadow-2xl animate-in fade-in duration-200">
            <div className="flex items-center justify-between pb-1 border-b border-indigo-500/20">
              <div className="flex items-center gap-1.5 text-[11px] font-mono font-bold text-indigo-300">
                <Sparkles size={12} className="text-indigo-400" />
                <span>TEST AI RE-ENGAGEMENT DISPATCH</span>
              </div>
              <button
                type="button"
                onClick={() => setIsTestDrawerOpen(false)}
                className="text-[10px] text-slate-400 hover:text-white cursor-pointer"
              >
                ✕
              </button>
            </div>

            <div className="space-y-1.5">
              <input
                type="text"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="Phone (e.g. 94712345678)"
                className="w-full px-2.5 py-1.5 rounded-lg bg-slate-900 border border-indigo-500/30 text-xs text-white placeholder-slate-500 font-mono focus:outline-none focus:border-indigo-400"
              />
              <input
                type="text"
                value={testTopic}
                onChange={(e) => setTestTopic(e.target.value)}
                placeholder="Topic: e.g. Blue Denim cargo shorts"
                className="w-full px-2.5 py-1.5 rounded-lg bg-slate-900 border border-indigo-500/30 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-indigo-400"
              />
            </div>

            <div className="flex items-center justify-between pt-1">
              <span className="text-[9px] font-mono text-slate-400">
                Uses Official Meta / Evolution API
              </span>
              <button
                type="button"
                disabled={isTesting || !testPhone.trim()}
                onClick={handleSendTestMessage}
                className="px-3 py-1 rounded-md bg-indigo-600 hover:bg-indigo-500 text-white text-[10px] font-bold flex items-center gap-1 cursor-pointer transition disabled:opacity-50"
              >
                <Send size={10} className={isTesting ? 'animate-spin' : ''} />
                <span>{isTesting ? 'Sending...' : '🧪 Send Test'}</span>
              </button>
            </div>

            {testResult && (
              <div
                className={`p-2 rounded-lg text-[10px] ${
                  testResult.success
                    ? 'bg-emerald-950/50 border border-emerald-500/30 text-emerald-300'
                    : 'bg-rose-950/50 border border-rose-500/30 text-rose-300'
                }`}
              >
                <div>{testResult.message}</div>
                {testResult.text && <div className="mt-1 italic text-slate-300">"{testResult.text}"</div>}
              </div>
            )}
          </div>
        )}

        {/* ── Interactive Live Pending Recovery Queue Drawer ── */}
        {isQueueOpen && (
          <div className="p-3 rounded-xl bg-slate-950/95 border border-purple-500/40 space-y-2.5 shadow-2xl animate-in fade-in duration-200">
            <div className="flex items-center justify-between pb-1.5 border-b border-purple-500/20">
              <div className="flex items-center gap-1.5 text-[11px] font-mono font-bold text-purple-300">
                <Clock size={12} className="text-purple-400 animate-pulse" />
                <span>PENDING RECOVERY QUEUE ({telemetry?.pendingQueue?.length || 0})</span>
              </div>
              <button
                type="button"
                onClick={fetchTelemetry}
                disabled={loadingTelemetry}
                className="text-[10px] font-mono text-purple-300 hover:text-white flex items-center gap-1 cursor-pointer transition active:scale-95"
                title="Refresh Queue"
              >
                <RefreshCw size={10} className={loadingTelemetry ? 'animate-spin text-purple-400' : ''} />
                <span>Refresh</span>
              </button>
            </div>

            {(!telemetry?.pendingQueue || telemetry.pendingQueue.length === 0) ? (
              <div className="py-4 text-center text-[11px] text-slate-500 italic">
                No dropped conversations waiting in queue right now.
              </div>
            ) : (
              <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                {telemetry.pendingQueue.map((item: any) => {
                  const convId = item.conversationId || item.id;
                  const custPhone = item.customerPhone || item.phone;
                  return (
                    <div
                      key={convId}
                      className="p-2.5 rounded-lg bg-slate-900/90 border border-purple-500/25 space-y-1.5 hover:border-purple-400/50 transition"
                    >
                      <div className="flex items-center justify-between text-[10px] font-mono">
                        <div className="flex items-center gap-1.5 text-white font-bold">
                          <User size={10} className="text-purple-400" />
                          <span>+{custPhone}</span>
                          {item.visitorName && <span className="text-slate-400 font-normal">({item.visitorName})</span>}
                        </div>
                        {item.status === 'ready' ? (
                          <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 flex items-center gap-0.5 shadow-[0_0_8px_rgba(16,185,129,0.3)]">
                            <Zap size={9} /> READY
                          </span>
                        ) : (
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30 flex items-center gap-0.5">
                            <Clock size={9} /> {item.hoursRemaining}h left
                          </span>
                        )}
                      </div>

                      <div className="flex items-center justify-between text-[10px]">
                        <span className="text-purple-300 font-mono text-[9px] bg-purple-950/60 px-1.5 py-0.5 rounded border border-purple-500/20">
                          {item.stageLabel}
                        </span>
                        <span className="text-slate-500 text-[9px] font-mono">
                          {item.hoursSinceActive}h inactive
                        </span>
                      </div>

                      {item.lastMessageText && (
                        <p className="text-[10px] text-slate-400 line-clamp-1 italic font-sans">
                          "{item.lastMessageText}"
                        </p>
                      )}

                      <div className="pt-1 flex items-center justify-end gap-1.5">
                        <button
                          type="button"
                          onClick={() => handleDismissCandidate(convId)}
                          className="px-2 py-0.5 rounded bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-400 hover:text-rose-300 text-[9px] font-mono cursor-pointer transition"
                          title="Dismiss candidate from follow-up queue"
                        >
                          Dismiss
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDispatchSingle(convId)}
                          disabled={dispatchingId === convId}
                          className="px-2.5 py-1 rounded-md bg-purple-600/30 hover:bg-purple-600/50 border border-purple-400/40 text-purple-200 hover:text-white text-[10px] font-bold flex items-center gap-1 cursor-pointer transition disabled:opacity-50 active:scale-95"
                          title="Send autonomous AI recovery message immediately"
                        >
                          <Send size={10} className={dispatchingId === convId ? 'animate-spin text-purple-300' : ''} />
                          <span>{dispatchingId === convId ? 'Sending...' : '⚡ Send Follow-Up Now'}</span>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        
        {/* Timing Delay Selector */}
        <div className="space-y-1.5">
          <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wider font-mono flex items-center justify-between">
            <span className="flex items-center gap-1.5">
              <Clock size={12} className="text-purple-400" />
              <span>RE-ENGAGEMENT TIMING DELAY</span>
            </span>
            <span className="text-purple-300 font-bold">{delayHours} Hours</span>
          </label>
          
          <div className="grid grid-cols-4 gap-1.5">
            {[6, 12, 24, 48].map((hours) => (
              <button
                key={hours}
                type="button"
                onClick={() => handleFieldChange('delayHours', hours)}
                className={`py-1.5 px-2 rounded-xl text-xs font-mono font-bold border transition cursor-pointer text-center ${
                  delayHours === hours
                    ? 'bg-purple-500/25 border-purple-400 text-purple-200 shadow-[0_0_12px_rgba(168,85,247,0.3)]'
                    : 'bg-slate-900/60 border-slate-800 text-slate-400 hover:text-slate-200 hover:bg-slate-800'
                }`}
              >
                {hours === 24 ? '24h (1 Day)' : `${hours}h`}
              </button>
            ))}
          </div>
          <p className="text-[10px] text-slate-500 italic">
            Wait duration after customer drops off before Luna initiates re-engagement.
          </p>
        </div>

        {/* Abandoned Trigger Scope Filters */}
        <div className="space-y-1.5 p-3 rounded-xl bg-slate-900/50 border border-purple-500/15">
          <span className="text-[10px] font-bold text-purple-300 uppercase tracking-wider font-mono flex items-center gap-1.5">
            <Zap size={11} className="text-purple-400" />
            <span>TRIGGER SCOPE (ABANDONED CONVERSATIONS)</span>
          </span>

          <div className="space-y-1.5 pt-1">
            <label className="flex items-center justify-between p-1.5 rounded-lg hover:bg-slate-800/40 transition cursor-pointer">
              <span className="text-[11px] text-slate-300 flex items-center gap-2">
                <Tag size={12} className="text-purple-400" />
                <span>Abandoned Product Inquiries & Price Checks</span>
              </span>
              <input
                type="checkbox"
                checked={filterAbandonedInquiries}
                onChange={(e) => handleFieldChange('filterAbandonedInquiries', e.target.checked)}
                className="w-4 h-4 rounded bg-slate-900 border-slate-700 text-purple-500 focus:ring-purple-400 cursor-pointer"
              />
            </label>

            <label className="flex items-center justify-between p-1.5 rounded-lg hover:bg-slate-800/40 transition cursor-pointer">
              <span className="text-[11px] text-slate-300 flex items-center gap-2">
                <MessageSquare size={12} className="text-emerald-400" />
                <span>Unconfirmed Shopping Orders (Checkout Drop-off)</span>
              </span>
              <input
                type="checkbox"
                checked={filterAbandonedOrders}
                onChange={(e) => handleFieldChange('filterAbandonedOrders', e.target.checked)}
                className="w-4 h-4 rounded bg-slate-900 border-slate-700 text-purple-500 focus:ring-purple-400 cursor-pointer"
              />
            </label>

            <label className="flex items-center justify-between p-1.5 rounded-lg hover:bg-slate-800/40 transition cursor-pointer">
              <span className="text-[11px] text-slate-300 flex items-center gap-2">
                <ShieldCheck size={12} className="text-cyan-400" />
                <span>Incomplete Appointment & Form Bookings</span>
              </span>
              <input
                type="checkbox"
                checked={filterUnfinishedForms}
                onChange={(e) => handleFieldChange('filterUnfinishedForms', e.target.checked)}
                className="w-4 h-4 rounded bg-slate-900 border-slate-700 text-purple-500 focus:ring-purple-400 cursor-pointer"
              />
            </label>
          </div>
        </div>

        {/* Safety & Anti-Spam Guardrails */}
        <div className="grid grid-cols-2 gap-2">
          {/* Quiet Hours Protection */}
          <div className="p-2.5 rounded-xl bg-slate-900/60 border border-slate-800 space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-mono font-bold text-slate-300 flex items-center gap-1">
                <Moon size={11} className="text-indigo-400" /> QUIET HOURS
              </span>
              <input
                type="checkbox"
                checked={quietHours}
                onChange={(e) => handleFieldChange('quietHours', e.target.checked)}
                className="w-3.5 h-3.5 rounded bg-slate-900 border-slate-700 text-purple-500 cursor-pointer"
              />
            </div>
            <p className="text-[9px] text-slate-400 leading-tight">
              Mutes 10 PM–8 AM. Queues delivery for 9 AM next morning.
            </p>
          </div>

          {/* Anti-Spam Single Message Cap */}
          <div className="p-2.5 rounded-xl bg-slate-900/60 border border-slate-800 space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-mono font-bold text-slate-300 flex items-center gap-1">
                <ShieldCheck size={11} className="text-emerald-400" /> 1-MESSAGE CAP
              </span>
              <span className="text-[9px] font-mono font-bold text-emerald-300 bg-emerald-950/60 px-1 py-0.2 rounded border border-emerald-500/30">
                ACTIVE
              </span>
            </div>
            <p className="text-[9px] text-slate-400 leading-tight">
              Strict 1 follow-up max per chat. Protects WhatsApp reputation.
            </p>
          </div>
        </div>

        {/* Optional Incentive / Promotion Hook */}
        <div className="p-3 rounded-xl bg-slate-900/50 border border-purple-500/15 space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-[11px] font-bold text-purple-300 uppercase tracking-wider font-mono flex items-center gap-1.5 cursor-pointer">
              <Gift size={12} className="text-pink-400" />
              <span>RECOVERY INCENTIVE / HOOK (OPTIONAL)</span>
            </label>
            <input
              type="checkbox"
              checked={includeIncentive}
              onChange={(e) => handleFieldChange('includeIncentive', e.target.checked)}
              className="w-4 h-4 rounded bg-slate-900 border-slate-700 text-purple-500 cursor-pointer"
            />
          </div>

          {includeIncentive && (
            <input
              type="text"
              value={incentiveText}
              onChange={(e) => handleFieldChange('incentiveText', e.target.value)}
              placeholder="e.g. Free express delivery if confirmed today!"
              className="w-full px-3 py-1.5 rounded-lg bg-slate-950 border border-slate-700 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-purple-400 font-sans transition"
            />
          )}
        </div>

        {/* Advanced AI Directive Toggle */}
        <div className="pt-1">
          <button
            type="button"
            onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
            className="flex items-center gap-1 text-[11px] font-bold text-slate-400 hover:text-purple-300 transition"
          >
            {isAdvancedOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            <span>Custom AI Guidance & Tone Directive</span>
          </button>

          {isAdvancedOpen && (
            <div className="mt-2 space-y-1.5 animate-in fade-in duration-200">
              <textarea
                rows={2}
                value={customTemplate}
                onChange={(e) => handleFieldChange('customTemplate', e.target.value)}
                placeholder="e.g. Always maintain a friendly, warm tone in Sinhala or Singlish, politely asking if they need any delivery or payment assistance."
                className="w-full p-2 rounded-lg bg-slate-950 border border-slate-800 text-[11px] text-slate-200 placeholder-slate-600 focus:outline-none focus:border-purple-400 font-sans transition"
              />
              <p className="text-[10px] text-slate-500">
                Luna dynamically crafts the exact message referencing the item discussed.
              </p>
            </div>
          )}
        </div>

      </div>

      {/* ── Footer ── */}
      <div className="px-4 py-2.5 bg-slate-950/80 border-t border-purple-500/10 rounded-b-2xl flex items-center justify-between text-[10px] font-mono text-slate-500">
        <span className="flex items-center gap-1.5 text-slate-400">
          <Sparkles size={11} className="text-purple-400" />
          <span>Recovered:</span>
          <span className="text-emerald-300 font-bold">{telemetry?.totalRecoveredCustomers || 0}</span>
          <span>({telemetry?.recoveryRatePercentage || 0}%)</span>
        </span>
        <span className="text-purple-300 font-bold">
          {delayHours}h Delay • {isEnabled ? 'Armed' : 'Muted'}
        </span>
      </div>
    </div>
  );
};

export default memo(FollowUpNode);
